import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import {
  useMonkeysEnvironment,
  useMonkeysPortalOptions,
  type MonkeysPortalContainer,
} from '../../../provider/context';
import { resolveBasePortalContainer } from '../BasePortal';

interface ResolveBaseOverlayPortalStateOptions {
  mounted: boolean;
  explicitDisabled?: boolean;
  explicitContainer?: MonkeysPortalContainer;
  providerDisabled?: boolean;
  providerContainer?: MonkeysPortalContainer;
  getDocument?: () => Document | null;
}

interface BaseOverlayPortalInlineState {
  mounted: boolean;
  disabled: boolean;
  shouldPortal: false;
  container: null;
  radixPortalProps: undefined;
}

interface BaseOverlayPortalReadyState {
  mounted: true;
  disabled: false;
  shouldPortal: true;
  container: Element;
  radixPortalProps: Readonly<{ container: Element }>;
}

export type BaseOverlayPortalState =
  | BaseOverlayPortalInlineState
  | BaseOverlayPortalReadyState;

export function resolveBaseOverlayPortalState({
  mounted,
  explicitDisabled,
  explicitContainer,
  providerDisabled,
  providerContainer,
  getDocument,
}: ResolveBaseOverlayPortalStateOptions): BaseOverlayPortalState {
  const disabled = explicitDisabled ?? providerDisabled ?? false;
  if (!mounted || disabled) {
    return {
      mounted,
      disabled,
      shouldPortal: false,
      container: null,
      radixPortalProps: undefined,
    };
  }

  const container = resolveBasePortalContainer({
    explicitProvided: explicitContainer !== undefined,
    explicitContainer,
    providerProvided: providerContainer !== undefined,
    providerContainer,
    getDocument,
  });

  if (!container) {
    return {
      mounted: true,
      disabled: false,
      shouldPortal: false,
      container: null,
      radixPortalProps: undefined,
    };
  }

  return {
    mounted: true,
    disabled: false,
    shouldPortal: true,
    container,
    radixPortalProps: Object.freeze({ container }),
  };
}

export interface UseBaseOverlayPortalOptions {
  container?: MonkeysPortalContainer;
  disabled?: boolean;
}

function getDefaultDocument(): Document | null {
  return globalThis.document ?? null;
}

export function useBaseOverlayPortal(
  options: UseBaseOverlayPortalOptions = {},
): BaseOverlayPortalState {
  const environment = useMonkeysEnvironment();
  const portalOptions = useMonkeysPortalOptions();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return resolveBaseOverlayPortalState({
    mounted,
    explicitDisabled: options.disabled,
    explicitContainer: options.container,
    providerDisabled: portalOptions.disabled,
    providerContainer: portalOptions.container,
    getDocument: environment.getDocument ?? getDefaultDocument,
  });
}

export interface BaseOverlayPortalProps extends UseBaseOverlayPortalOptions {
  children?: ReactNode;
}

export function renderBaseOverlayPortalContent(
  children: ReactNode,
  portal: BaseOverlayPortalState,
) {
  return portal.shouldPortal
    ? createPortal(children, portal.container)
    : <>{children}</>;
}

export function BaseOverlayPortal({
  children,
  container,
  disabled,
}: BaseOverlayPortalProps) {
  const portal = useBaseOverlayPortal({ container, disabled });
  return renderBaseOverlayPortalContent(children, portal);
}
